import { useState } from 'react';
import { AlertTriangle, Info, AlertCircle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSystemBanners } from '../../hooks/useSystemBanners';
import type { SystemBanner as SystemBannerType } from '../../hooks/useSystemBanners';

const priorityConfig = {
  urgent: {
    icon: AlertCircle,
    container: 'bg-red-500/15 border-red-500/30',
    icon_color: 'text-red-600 dark:text-red-400',
    text: 'text-red-900 dark:text-red-100',
    badge: 'bg-red-500/20 text-red-700 dark:text-red-300',
    pulse: true,
  },
  warning: {
    icon: AlertTriangle,
    container: 'bg-amber-500/12 border-amber-500/25',
    icon_color: 'text-amber-600 dark:text-amber-400',
    text: 'text-amber-900 dark:text-amber-100',
    badge: 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
    pulse: false,
  },
  info: {
    icon: Info,
    container: 'bg-blue-500/12 border-blue-500/25',
    icon_color: 'text-blue-600 dark:text-blue-400',
    text: 'text-blue-900 dark:text-blue-100',
    badge: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
    pulse: false,
  },
} as const;

function BannerItem({ banner, isExpanded, onToggle, onDismiss }: {
  banner: SystemBannerType;
  isExpanded: boolean;
  onToggle: () => void;
  onDismiss: () => void;
}) {
  const config = priorityConfig[banner.priority] || priorityConfig.info;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'rounded-xl border backdrop-blur-sm transition-all duration-200 overflow-hidden',
        config.container,
      )}
    >
      <div
        className="flex items-center gap-3 p-3 cursor-pointer select-none"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div className={cn('flex-shrink-0', config.pulse && 'animate-pulse')}>
          <Icon className={cn('h-5 w-5', config.icon_color)} />
        </div>
        <span className={cn('flex-1 text-sm font-medium leading-snug', config.text)}>
          {banner.title}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isExpanded ? (
            <ChevronUp className={cn('h-4 w-4', config.text, 'opacity-60')} />
          ) : (
            <ChevronDown className={cn('h-4 w-4', config.text, 'opacity-60')} />
          )}
          {banner.priority !== 'urgent' && (
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              aria-label="Stäng"
            >
              <X className={cn('h-3.5 w-3.5', config.text, 'opacity-60')} />
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className={cn(
          'px-3 pb-3 pl-11 text-sm leading-relaxed animate-fade-in',
          config.text, 'opacity-80'
        )}>
          {banner.message}
        </div>
      )}
    </div>
  );
}

export function SystemBanner() {
  const { banners, isLoading } = useSystemBanners();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  if (isLoading || banners.length === 0) return null;

  const visibleBanners = banners.filter(b => !dismissedIds.has(b.id));
  if (visibleBanners.length === 0) return null;

  return (
    <div className="space-y-2 px-4 pt-2 pb-1">
      {visibleBanners.map((banner) => (
        <BannerItem
          key={banner.id}
          banner={banner}
          isExpanded={expandedId === banner.id}
          onToggle={() => setExpandedId(prev => prev === banner.id ? null : banner.id)}
          onDismiss={() => setDismissedIds(prev => new Set(prev).add(banner.id))}
        />
      ))}
    </div>
  );
}
